import React from "react";
import { Star } from "lucide-react";
import { getStatusColors, formatRate } from "./performanceUtils";

const SPOTLIGHT_CONFIGS = [
  {
    status: "Top Performer",
    label: "Top Performer",
    pick: (candidates) =>
      candidates.reduce((best, u) =>
        u.conversionRate > best.conversionRate ? u : best
      ),
  },
  {
    status: "Underdelivering",
    label: "Underdelivering",
    pick: (candidates) =>
      candidates.reduce((best, u) =>
        u.totalEffort > best.totalEffort ? u : best
      ),
  },
  {
    status: "Unacceptable Output",
    label: "Unacceptable Output",
    pick: (candidates) =>
      candidates.reduce((best, u) =>
        u.totalOutput > best.totalOutput ? u : best
      ),
  },
];

function StatusLabel({ status, label, colors }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${colors.bg} ${colors.text}`}
    >
      {status === "Top Performer" ? (
        <Star size={12} fill="currentColor" strokeWidth={0} />
      ) : (
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.dot }} />
      )}
      {label}
    </span>
  );
}

function SpotlightCard({ status, label, user, projectLabel }) {
  const colors = getStatusColors(status);

  if (!user) {
    return (
      <div
        className="rounded-xl border-r-[3px] bg-white p-5 shadow-md dark:bg-slate-800 dark:shadow-black/30"
        style={{ borderRightColor: colors.dot }}
      >
        <div className="flex justify-end">
          <StatusLabel status={status} label={label} colors={colors} />
        </div>
        <p className="mt-4 text-sm text-gray-400 dark:text-slate-400">No data available.</p>
      </div>
    );
  }

  const email = user.user?.email || "—";
  const initial = email !== "—" ? email.charAt(0).toUpperCase() : "?";

  return (
    <div
      className="rounded-xl border-l-2 bg-white p-5 shadow-md dark:bg-slate-800 dark:shadow-black/30"
      style={{ borderLeftColor: colors.dot }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${colors.bg} ${colors.text}`}
          >
            {initial}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-slate-100">{email}</p>
            <p className="truncate text-xs text-gray-500 dark:text-slate-400">{projectLabel}</p>
          </div>
        </div>
        <StatusLabel status={status} label={label} colors={colors} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-gray-100 dark:border-slate-700 pt-4">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-400">Output</p>
          <p className={`mt-1 text-xl font-bold ${colors.text}`}>{user.totalOutput}</p>
        </div>
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-400">Effort</p>
          <p className="mt-1 text-xl font-bold text-gray-800 dark:text-slate-100">{user.totalEffort}</p>
        </div>
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-400">Rate</p>
          <p className="mt-1 text-xl font-bold text-gray-800 dark:text-slate-100">{formatRate(user.conversionRate)}</p>
        </div>
      </div>
    </div>
  );
}

function SpotlightSection({ data, isLoading }) {
  if (isLoading) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/50 dark:shadow-none">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Spotlight</h3>
        <p className="mt-2 text-sm text-gray-400 dark:text-slate-400">Loading...</p>
      </section>
    );
  }

  const spotlights = SPOTLIGHT_CONFIGS.map(({ status, label, pick }) => {
    const candidates = data.filter((u) => u.status === status);
    return { status, label, user: candidates.length > 0 ? pick(candidates) : null };
  });

  const projectLabel = (user) =>
    user?.project?.projectName || (user?.projectId ? `Project #${user.projectId}` : "All Projects");

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/50 dark:shadow-none">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Spotlight</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
        Notable performers across each classification.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-1">
        {spotlights.map(({ status, label, user }) => (
          <SpotlightCard
            key={status}
            status={status}
            label={label}
            user={user}
            projectLabel={user ? projectLabel(user) : ""}
          />
        ))}
      </div>
    </section>
  );
}

export default SpotlightSection;
